import React, { useState, useEffect } from 'react';
import useApi from '../../../hooks/useApi';
import useSnackbar from '../../../hooks/useSnackbar';
import LoadingOverlay from '../../../loadingOverlay/LoadingOverlay';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import buildUrl from '../../../buildUrl';
import { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';

const rowsPerPageOptions = [10, 25, 50];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },

    '&:last-child td, &:last-child th': {
        border: 0
    }
}));

const EmptyTable = ({ isLoading, filtersApplied }) => {
    if (isLoading) {
        return null;
    }

    return (
        <Box sx={{ padding: 6, textAlign: 'center' }}>
            {filtersApplied ? (
                <Typography>No data</Typography>
            ) : (
                <Typography>No Nps Comments.</Typography>
            )}
        </Box>
    );
};

const Comments = ({ selectedYear, selectedQuarter, searchValue }) => {
    const { showSnackbar } = useSnackbar();
    const { handleGet } = useApi();
    const [npsComments, setNpsComments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
    const [count, setCount] = useState([]);

    const fetchNpsCommentsCount = async () => {
        setIsLoading(true);

        try {
            const params = {
                search: searchValue
            };

            if (selectedYear.length > 0) {
                params.year = selectedYear;
            }

            if (selectedQuarter.length > 0) {
                params.quarter = selectedQuarter;
            }

            const url = buildUrl('superadmin/accounts/nps-comments/count', params);

            const response = await handleGet(url);
            const data = await response.json();

            setCount(data);
        }
        catch (error) {
            showSnackbar('Error fetching NPS Comments count', 'error');
        }

        setIsLoading(false);
    };

    const fetchNpsComments = async () => {
        setIsLoading(true);

        try {
            const params = {
                search: searchValue,
                skip: page * rowsPerPage,
                take: rowsPerPage
            };

            if (selectedYear.length > 0) {
                params.year = selectedYear;
            }

            if (selectedQuarter.length > 0) {
                params.quarter = selectedQuarter;
            }

            const url = buildUrl('superadmin/accounts/nps-comments', params);

            const response = await handleGet(url);
            const data = await response.json();

            setNpsComments(data);
        }
        catch (error) {
            showSnackbar('Error fetching NPS Comments', 'error');
        }

        setIsLoading(false);
    };

    useEffect(() => {
        fetchNpsCommentsCount();
    }, [selectedYear, selectedQuarter, rowsPerPage, searchValue]);

    useEffect(() => {
        fetchNpsComments();
    }, [selectedYear, selectedQuarter, rowsPerPage, page, searchValue]);

    useEffect(() => {
        setPage(0);
    }, [rowsPerPage, searchValue]);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <>
            {npsComments.length > 0 ? (
                <>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell component="th" scope="row" align="left" sx={{ width: '20%' }}>
                                        Email Address
                                    </TableCell>
                                    <TableCell component="th" scope="row" sx={{ width: '10%' }}>
                                        Created
                                    </TableCell>
                                    <TableCell component="th" scope="row" align="center" sx={{ width: '10%' }}>
                                        Score
                                    </TableCell>
                                    <TableCell component="th" scope="row" align="center" sx={{ width: '10%' }}>
                                        Period
                                    </TableCell>
                                    <TableCell component="th" scope="row" align="left" sx={{ width: '50%' }}>
                                        Response
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {npsComments.map((row, index) => (
                                    <StyledTableRow key={index}>
                                        <StyledTableCell component="th" scope="row">
                                            {row.userEmailAddress}
                                        </StyledTableCell>
                                        <StyledTableCell component="th" scope="row">
                                            {format(new Date(row.createdDate), 'dd/MM/yyyy HH:mm')}
                                        </StyledTableCell>
                                        <StyledTableCell
                                            align="center"
                                            sx={{
                                                backgroundColor:
                                                    row.score === null ? 'inherit' :
                                                        row.score <= 6 ? '#f44336' :
                                                            row.score <= 8 ? '#ffeb3b' :
                                                                'lightgreen'
                                            }}
                                        >
                                            {row.score}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">{row.period}</StyledTableCell>
                                        <StyledTableCell align="left">{row.response || '-'}</StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={count}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={e => setRowsPerPage(e.target.value)}
                    />
                </>
            ) : (
                <EmptyTable
                    isLoading={isLoading}
                    filtersApplied={selectedYear || selectedQuarter}
                />
            )}
        </>
    );
};

export default Comments;